import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { isAxiosError } from 'axios';
import { Spinner } from '@/components/ui';
import { HullRenderer } from '@/utils/hullRenderer';
import { calculateRegionHulls, getRegionHulls } from '@/services/annotationApi';
import { getPoseDataWithRetry, extractPoseData } from '@/services/videoApi';
import type { Annotation } from './RegionAnnotator';

/**
 * Hull Visualization Component
 *
 * Displays convex hulls for tracked annotations as an overlay on video canvas.
 * Handles hull loading, calculation, and rendering independently from the main annotator.
 *
 * This component encapsulates:
 * - Hull renderer instance management
 * - Loading existing hulls from backend
 * - Calculating new hulls for tracked annotations
 * - Hull display controls (visibility, opacity)
 * - Rendering hulls on canvas overlay
 */

export interface HullVisualizationProps {
  videoId: string;
  annotations: Annotation[];  // All annotations (component filters for tracked ones)
  currentFrame: number;
  videoWidth: number;
  videoHeight: number;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onHullsLoaded?: (hulls: Record<string, Record<string, number[][]>>) => void;
}

export interface HullVisualizationHandle {
  calculateHulls: (annotation: Annotation) => Promise<void>;
  removeHull: (annotationId: string) => void;
  refreshHulls: () => Promise<void>;
  getRenderer: () => HullRenderer;
}

const HullVisualization = forwardRef<HullVisualizationHandle, HullVisualizationProps>(
  ({ videoId, annotations, currentFrame, videoWidth, videoHeight, canvasRef, onHullsLoaded }, ref) => {
    // Hull rendering state
    const [hullRenderer] = useState(() => new HullRenderer());
    const [showHulls, setShowHulls] = useState(true);
    const [hullOpacity, setHullOpacity] = useState(0.6);
    const [isLoadingHulls, setIsLoadingHulls] = useState(false);
    const [hullStatusMessage, setHullStatusMessage] = useState<string | null>(null);

    /**
     * Check if pose data exists for the video, extract if not.
     *
     * Uses getPoseDataWithRetry so that a 202 Accepted response (backend
     * scheduling background extraction) is polled transparently rather than
     * throwing immediately. Falls back to explicit POST extraction only on 404.
     */
    const ensurePoseDataExists = useCallback(async (): Promise<boolean> => {
      try {
        // getPoseDataWithRetry handles 202 polling automatically
        await getPoseDataWithRetry(videoId);
        return true;
      } catch (error) {
        // If not found (404), trigger explicit extraction
        if (isAxiosError(error) && error.response?.status === 404) {
          try {
            setHullStatusMessage('Extracting pose data from video...');
            await extractPoseData(videoId);
            setHullStatusMessage(null);
            return true;
          } catch {
            throw new Error('Failed to extract pose data');
          }
        }
        throw error;
      }
    }, [videoId]);

    /**
     * Load existing hulls for tracked annotations on mount and when annotations change
     */
    useEffect(() => {
      const loadExistingHulls = async () => {
        for (const ann of annotations) {
          if (ann.tracking && ann.id) {
            try {
              const hullData = await getRegionHulls(videoId, ann.id);
              hullRenderer.addRegion(ann.id, {
                convex_hulls: hullData.hulls,
                color: ann.color,
                label: ann.description
              });

              // Notify parent if callback provided
              if (onHullsLoaded) {
                onHullsLoaded({ [ann.id]: hullData.hulls });
              }
            } catch {
              // Hulls may not exist yet for this annotation
              console.debug(`No existing hulls for annotation ${ann.id}`);
            }
          }
        }
      };

      if (annotations.length > 0) {
        loadExistingHulls();
      }
    }, [videoId, annotations, hullRenderer, onHullsLoaded]);

    /**
     * Calculate hulls for a newly tracked annotation
     */
    const calculateHullsForAnnotation = useCallback(async (annotation: Annotation) => {
      if (!annotation.tracking || !annotation.id) return;

      setIsLoadingHulls(true);
      setHullStatusMessage('Calculating tracking data...');

      try {
        const poseDataExists = await ensurePoseDataExists();
        if (!poseDataExists) throw new Error('Pose data not available');

        const hullResult = await calculateRegionHulls(videoId, annotation.id);
        if (hullResult) {
          hullRenderer.addRegion(annotation.id, {
            convex_hulls: hullResult.annotation.convex_hulls,
            color: annotation.color,
            label: annotation.description
          });

          // Notify parent if callback provided
          if (onHullsLoaded) {
            onHullsLoaded({ [annotation.id]: hullResult.annotation.convex_hulls });
          }

          setHullStatusMessage(null);
        }
      } catch (error) {
        console.error('Failed to calculate hulls:', error);
        setHullStatusMessage('Failed to calculate tracking data');
        setTimeout(() => setHullStatusMessage(null), 5000);
      } finally {
        setIsLoadingHulls(false);
      }
    }, [videoId, hullRenderer, ensurePoseDataExists, onHullsLoaded]);

    /**
     * Remove hulls for a specific annotation
     */
    const removeHull = useCallback((annotationId: string) => {
      hullRenderer.removeRegion(annotationId);
    }, [hullRenderer]);

    /**
     * Refresh all hulls (recalculate for all tracked annotations)
     */
    const refreshHulls = useCallback(async () => {
      const trackedAnnotations = annotations.filter(ann => ann.tracking && ann.id);
      for (const ann of trackedAnnotations) {
        await calculateHullsForAnnotation(ann);
      }
    }, [annotations, calculateHullsForAnnotation]);

    /**
     * Expose methods to parent component via ref
     */
    useImperativeHandle(ref, () => ({
      calculateHulls: calculateHullsForAnnotation,
      removeHull,
      refreshHulls,
      getRenderer: () => hullRenderer,
    }), [calculateHullsForAnnotation, removeHull, refreshHulls, hullRenderer]);

    /**
     * Render hulls on the canvas overlay
     */
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear the canvas
      ctx.clearRect(0, 0, videoWidth, videoHeight);

      // Render convex hulls
      hullRenderer.setVisible(showHulls);
      hullRenderer.setOpacity(hullOpacity);
      hullRenderer.renderFrame(ctx, currentFrame, videoWidth, videoHeight);
    }, [currentFrame, videoWidth, videoHeight, showHulls, hullOpacity, hullRenderer, canvasRef]);

    return (
      <div className="border border-border-subtle rounded-lg p-4 bg-dark-card">
        <h4 className="text-sm font-semibold mb-3 text-text-primary">Region Tracking</h4>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showHulls}
              onChange={(e) => setShowHulls(e.target.checked)}
              className="w-4 h-4 rounded border-border-strong text-blue-600"
            />
            <span className="text-sm text-text-primary">Show tracked regions</span>
          </label>

          <div className="space-y-1">
            <label className="text-sm text-text-secondary">
              Opacity: {Math.round(hullOpacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={hullOpacity * 100}
              onChange={(e) => setHullOpacity(parseInt(e.target.value) / 100)}
              disabled={!showHulls}
              className="w-full h-2 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {isLoadingHulls && hullStatusMessage && (
            <div className="flex items-center gap-2 text-xs text-info bg-info/10 p-2 rounded border border-info/30">
              <Spinner className="h-4 w-4" />
              <span>{hullStatusMessage}</span>
            </div>
          )}

          {hullRenderer.getRegionCount() > 0 && (
            <div className="text-xs text-success bg-success/10 p-2 rounded border border-success/30">
              {hullRenderer.getRegionCount()} region{hullRenderer.getRegionCount() !== 1 ? 's' : ''} tracked
            </div>
          )}
        </div>
      </div>
    );
  }
);

HullVisualization.displayName = 'HullVisualization';

export default HullVisualization;
